import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

import {
  createEnvironmentMaterials,
  disposeMaterialSet,
  type EnvironmentMaterialSet,
} from './materials';
import {
  DEFAULT_PLOT_DIMENSIONS,
  createProceduralEnvironment,
  createSharedEnvironmentGeometry,
  disposeSharedEnvironmentGeometry,
  type PlotDimensions,
  type SharedEnvironmentGeometry,
} from './procedural';
import {
  clampPixelRatio,
  getQualityProfile,
  type QualityProfile,
  type QualityTier,
} from './quality';
import { SceneRootManager, type ReplaceRootOptions } from './roots';
import {
  DEFAULT_ENVIRONMENT_THEME_ID,
  getEnvironmentTheme,
  type EnvironmentThemeDefinition,
  type EnvironmentThemeId,
} from './themes';

export interface RendererLike {
  readonly domElement: HTMLCanvasElement;
  readonly shadowMap: { enabled: boolean; type: THREE.ShadowMapType };
  outputColorSpace: string;
  toneMapping: THREE.ToneMapping;
  toneMappingExposure: number;
  setPixelRatio(value: number): void;
  setSize(width: number, height: number, updateStyle?: boolean): void;
  render(scene: THREE.Scene, camera: THREE.Camera): void;
  dispose(): void;
}

export type RendererFactory = (parameters: THREE.WebGLRendererParameters) => RendererLike;

export interface BunchaSceneOptions {
  readonly canvas: HTMLCanvasElement;
  readonly qualityTier?: QualityTier;
  readonly themeId?: EnvironmentThemeId;
  readonly plot?: PlotDimensions;
  readonly rendererFactory?: RendererFactory;
}

interface SkyResources {
  readonly sky: THREE.Mesh<THREE.SphereGeometry, THREE.ShaderMaterial>;
  readonly environment: THREE.Texture;
  dispose(): void;
}

export interface ShadowEnvelope {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
  readonly near: number;
  readonly far: number;
}

export function sunDirectionForTheme(theme: EnvironmentThemeDefinition): THREE.Vector3 {
  const elevation = THREE.MathUtils.degToRad(theme.sky.elevationDegrees);
  const azimuth = THREE.MathUtils.degToRad(theme.sky.azimuthDegrees);
  return new THREE.Vector3(
    Math.cos(elevation) * Math.sin(azimuth),
    Math.sin(elevation),
    Math.cos(elevation) * Math.cos(azimuth),
  ).normalize();
}

function validBounds(bounds: THREE.Box3): boolean {
  return (
    !bounds.isEmpty() &&
    [bounds.min.x, bounds.min.y, bounds.min.z, bounds.max.x, bounds.max.y, bounds.max.z].every(
      Number.isFinite,
    )
  );
}

function fallbackShadowBounds(plot: PlotDimensions): THREE.Box3 {
  return new THREE.Box3(
    new THREE.Vector3(-plot.width / 2, -1, -plot.depth / 2),
    new THREE.Vector3(plot.width / 2, Math.max(54, plot.depth * 0.7), plot.depth / 2),
  );
}

export function calculateShadowEnvelope(
  inputBounds: THREE.Box3,
  sunDirection: THREE.Vector3,
  plot: PlotDimensions = DEFAULT_PLOT_DIMENSIONS,
): ShadowEnvelope {
  const bounds = (validBounds(inputBounds) ? inputBounds : fallbackShadowBounds(plot))
    .clone()
    .expandByScalar(4);
  const center = bounds.getCenter(new THREE.Vector3());
  const size = bounds.getSize(new THREE.Vector3());
  const distance = Math.max(80, size.length() * 1.1);
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.5, distance * 3);
  camera.position.copy(center).addScaledVector(sunDirection, distance);
  camera.up.set(0, 1, 0);
  camera.lookAt(center);
  camera.updateMatrixWorld(true);
  const lightSpace = camera.matrixWorldInverse;
  const minimum = new THREE.Vector3(
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
  );
  const maximum = new THREE.Vector3(
    Number.NEGATIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
  );
  for (const x of [bounds.min.x, bounds.max.x])
    for (const y of [bounds.min.y, bounds.max.y])
      for (const z of [bounds.min.z, bounds.max.z]) {
        const point = new THREE.Vector3(x, y, z).applyMatrix4(lightSpace);
        minimum.min(point);
        maximum.max(point);
      }
  return Object.freeze({
    left: minimum.x,
    right: maximum.x,
    top: maximum.y,
    bottom: minimum.y,
    near: Math.max(0.5, -maximum.z),
    far: Math.max(1, -minimum.z),
  });
}

function createSkyResources(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  theme: EnvironmentThemeDefinition,
): SkyResources {
  const sunDirection = sunDirectionForTheme(theme);
  const skyMaterial = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      topColor: { value: new THREE.Color(theme.colors.skyTop) },
      horizonColor: { value: new THREE.Color(theme.colors.skyHorizon) },
      sunColor: { value: new THREE.Color(theme.lighting.sunColor) },
      sunDirection: { value: sunDirection },
      sunGlowStrength: { value: theme.lighting.sunGlowStrength },
      horizonHaze: { value: theme.lighting.horizonHaze },
    },
    vertexShader: `
      varying vec3 vWorldPosition;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 topColor;
      uniform vec3 horizonColor;
      uniform vec3 sunColor;
      uniform vec3 sunDirection;
      uniform float sunGlowStrength;
      uniform float horizonHaze;
      varying vec3 vWorldPosition;
      void main() {
        vec3 direction = normalize(vWorldPosition);
        float h = clamp(direction.y * 0.72 + 0.38, 0.0, 1.0);
        float horizon = 1.0 - smoothstep(0.0, 0.34, abs(direction.y));
        float sun = pow(max(dot(direction, normalize(sunDirection)), 0.0), 180.0);
        float glow = pow(max(dot(direction, normalize(sunDirection)), 0.0), 18.0);
        vec3 color = mix(horizonColor, topColor, smoothstep(0.0, 1.0, h));
        color = mix(color, horizonColor, horizon * horizonHaze);
        color += sunColor * (sun * 0.72 + glow * 0.12) * sunGlowStrength;
        float dither = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
        color += (dither - 0.5) / 255.0;
        gl_FragColor = vec4(color, 1.0);
      }
    `,
  });
  const sky = new THREE.Mesh(new THREE.SphereGeometry(450, 32, 18), skyMaterial);
  sky.name = `sky:${theme.id}`;
  sky.renderOrder = -100;

  // Keep the visible gradient independent from image-based lighting. Capturing
  // an analytical Sky into PMREM caused the iOS black-material regression;
  // RoomEnvironment is Three.js's documented IBL source and the Phase 0 path.
  const lightingEnvironment = new RoomEnvironment();
  const pmrem = new THREE.PMREMGenerator(renderer);
  const environmentTarget = pmrem.fromScene(lightingEnvironment, 0.04);
  lightingEnvironment.dispose();
  scene.add(sky);
  scene.environment = environmentTarget.texture;
  return {
    sky,
    environment: environmentTarget.texture,
    dispose: () => {
      scene.remove(sky);
      if (scene.environment === environmentTarget.texture) scene.environment = null;
      environmentTarget.dispose();
      sky.geometry.dispose();
      sky.material.dispose();
      pmrem.dispose();
    },
  };
}

export class BunchaSceneService {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(42, 16 / 9, 0.1, 1_000);
  readonly content = new SceneRootManager('ride-content');
  readonly renderer: RendererLike;
  readonly quality: QualityProfile;
  #plot: PlotDimensions;

  #theme: EnvironmentThemeDefinition;
  #environmentRoot = new THREE.Group();
  #environmentMaterials: EnvironmentMaterialSet;
  #environmentGeometry: SharedEnvironmentGeometry;
  #skyResources: SkyResources | null = null;
  #lights = new THREE.Group();
  #sun: THREE.DirectionalLight | null = null;
  #shadowBounds = new THREE.Box3();
  #themeFog: THREE.Fog | null = null;
  #fogEnabled = true;
  #disposed = false;

  constructor(options: BunchaSceneOptions) {
    this.quality = getQualityProfile(options.qualityTier ?? 'high');
    this.#plot = Object.freeze({ ...(options.plot ?? DEFAULT_PLOT_DIMENSIONS) });
    this.#shadowBounds = fallbackShadowBounds(this.plot);
    this.#theme = getEnvironmentTheme(options.themeId ?? DEFAULT_ENVIRONMENT_THEME_ID);
    const rendererFactory =
      options.rendererFactory ?? ((parameters) => new THREE.WebGLRenderer(parameters));
    this.renderer = rendererFactory({
      canvas: options.canvas,
      antialias: this.quality.tier !== 'low',
      alpha: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.shadowMap.enabled = this.quality.shadows;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.camera.position.set(
      this.plot.width * 0.8,
      Math.max(54, this.plot.depth * 0.82),
      this.plot.depth * 1.2,
    );
    this.camera.lookAt(0, 7, 0);
    this.scene.add(this.content.host);
    this.#environmentMaterials = createEnvironmentMaterials(this.#theme);
    this.#environmentGeometry = createSharedEnvironmentGeometry(this.quality, this.plot);
    this.#installTheme(this.#theme);
  }

  get theme(): EnvironmentThemeDefinition {
    return this.#theme;
  }

  get plot(): PlotDimensions {
    return this.#plot;
  }

  setPlot(plot: PlotDimensions): void {
    this.#assertActive();
    const next = Object.freeze({ ...plot });
    if (
      next.width === this.#plot.width &&
      next.depth === this.#plot.depth &&
      (next.centerX ?? 0) === (this.#plot.centerX ?? 0) &&
      (next.centerZ ?? 0) === (this.#plot.centerZ ?? 0) &&
      (next.groundElevation ?? 0) === (this.#plot.groundElevation ?? 0)
    )
      return;
    this.#uninstallEnvironment();
    disposeSharedEnvironmentGeometry(this.#environmentGeometry);
    this.#plot = next;
    this.#shadowBounds = fallbackShadowBounds(this.#plot);
    this.#environmentMaterials = createEnvironmentMaterials(this.#theme);
    this.#environmentGeometry = createSharedEnvironmentGeometry(this.quality, this.#plot);
    this.#installTheme(this.#theme);
  }

  get environmentRoot(): THREE.Group {
    return this.#environmentRoot;
  }

  setTheme(id: EnvironmentThemeId): void {
    this.#assertActive();
    if (id === this.#theme.id) return;
    this.#uninstallEnvironment();
    this.#theme = getEnvironmentTheme(id);
    this.#environmentMaterials = createEnvironmentMaterials(this.#theme);
    this.#installTheme(this.#theme);
  }

  setFogEnabled(enabled: boolean): void {
    this.#assertActive();
    if (this.#fogEnabled === enabled) return;
    this.#fogEnabled = enabled;
    this.scene.fog = enabled ? this.#themeFog : null;
  }

  replaceContentRoot(nextRoot: THREE.Group, options?: ReplaceRootOptions): THREE.Group {
    this.#assertActive();
    const previous = this.content.replaceRoot(nextRoot, options);
    this.updateShadowBounds(new THREE.Box3().setFromObject(this.content.host));
    return previous;
  }

  updateShadowBounds(bounds: THREE.Box3): void {
    this.#assertActive();
    this.#shadowBounds = (validBounds(bounds) ? bounds : fallbackShadowBounds(this.plot)).clone();
    const sun = this.#sun;
    if (!sun) return;
    const direction = sunDirectionForTheme(this.#theme);
    const center = this.#shadowBounds.getCenter(new THREE.Vector3());
    const size = this.#shadowBounds.getSize(new THREE.Vector3());
    const distance = Math.max(80, size.length() * 1.1);
    sun.position.copy(center).addScaledVector(direction, distance);
    sun.target.position.copy(center);
    sun.target.updateMatrixWorld(true);
    const envelope = calculateShadowEnvelope(this.#shadowBounds, direction, this.plot);
    const camera = sun.shadow.camera;
    camera.left = envelope.left;
    camera.right = envelope.right;
    camera.top = envelope.top;
    camera.bottom = envelope.bottom;
    camera.near = envelope.near;
    camera.far = envelope.far;
    camera.updateProjectionMatrix();
    sun.shadow.needsUpdate = true;
  }

  resize(width: number, height: number, devicePixelRatio = globalThis.devicePixelRatio ?? 1): void {
    this.#assertActive();
    const safeWidth = Math.max(1, Math.floor(width));
    const safeHeight = Math.max(1, Math.floor(height));
    this.camera.aspect = safeWidth / safeHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(clampPixelRatio(devicePixelRatio, this.quality));
    this.renderer.setSize(safeWidth, safeHeight, false);
  }

  render(): void {
    this.#assertActive();
    this.renderer.render(this.scene, this.camera);
  }

  dispose(options: { readonly disposeContent?: boolean } = {}): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#uninstallEnvironment();
    disposeSharedEnvironmentGeometry(this.#environmentGeometry);
    if (options.disposeContent) {
      const empty = new THREE.Group();
      this.content.replaceRoot(empty, { disposePrevious: true });
    }
    this.scene.remove(this.content.host);
    this.renderer.dispose();
  }

  #installTheme(theme: EnvironmentThemeDefinition): void {
    this.scene.background = new THREE.Color(theme.colors.background);
    this.scene.environmentIntensity = theme.lighting.environmentIntensity;
    const plotDiagonal = Math.hypot(this.plot.width, this.plot.depth);
    this.#themeFog = theme.fog
      ? new THREE.Fog(
          theme.colors.fog,
          plotDiagonal * theme.fog.nearPlotDiagonals,
          plotDiagonal * theme.fog.farPlotDiagonals,
        )
      : null;
    this.scene.fog = this.#fogEnabled ? this.#themeFog : null;
    this.renderer.toneMappingExposure = theme.lighting.exposure;
    this.#environmentRoot = createProceduralEnvironment({
      theme,
      quality: this.quality,
      materials: this.#environmentMaterials,
      geometries: this.#environmentGeometry,
      plot: this.plot,
    });
    this.scene.add(this.#environmentRoot);
    this.#lights = this.#createLights(theme);
    this.scene.add(this.#lights);
    this.updateShadowBounds(this.#shadowBounds);
    if (this.renderer instanceof THREE.WebGLRenderer) {
      this.#skyResources = createSkyResources(this.renderer, this.scene, theme);
    }
  }

  #createLights(theme: EnvironmentThemeDefinition): THREE.Group {
    const root = new THREE.Group();
    root.name = `lights:${theme.id}`;
    const hemisphere = new THREE.HemisphereLight(
      theme.lighting.hemisphereSky,
      theme.lighting.hemisphereGround,
      theme.lighting.hemisphereIntensity,
    );
    hemisphere.name = 'hemisphere';
    const sun = new THREE.DirectionalLight(theme.lighting.sunColor, theme.lighting.sunIntensity);
    sun.name = 'sun';
    sun.castShadow = this.quality.shadows;
    sun.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize);
    sun.shadow.bias = -0.00035;
    sun.shadow.normalBias = 0.035;
    sun.shadow.radius = 1.8;
    const fill = new THREE.DirectionalLight(theme.lighting.fillColor, theme.lighting.fillIntensity);
    fill.name = 'fill';
    fill.position.set(-24, 18, -20);
    fill.castShadow = false;
    this.#sun = sun;
    root.add(hemisphere, sun, sun.target, fill);
    return root;
  }

  #uninstallEnvironment(): void {
    this.scene.remove(this.#environmentRoot, this.#lights);
    this.#skyResources?.dispose();
    this.#skyResources = null;
    // Directional-light shadow render targets are allocated lazily by the
    // renderer and are not released merely by removing the light from the
    // scene. Theme/plot replacement owns this light, so release its maps here
    // before dropping the reference.
    this.#sun?.shadow.dispose();
    this.#sun = null;
    this.#themeFog = null;
    disposeMaterialSet(this.#environmentMaterials);
    this.scene.fog = null;
  }

  #assertActive(): void {
    if (this.#disposed) throw new Error('BunchaSceneService has been disposed.');
  }
}
